import {
  createEffect,
  createSignal,
  JSXElement,
  mergeProps,
  Show
} from 'solid-js';
import { useQuery } from '@tanstack/solid-query';
import { miscConstants } from '~/consts.ts';
import { setGlobalStore } from '~/stores';
import {
  createPortalSession,
  getPortalSessionDetails,
  getPortalSessionDetailsQuery,
  getPortalSessionQuery,
  retryAuthorizedRequests
} from '~/api';
import SkLoader from '~/components/ui/SkLoader';
import { AppErrors } from '~/components/ErrorBoundaries/utils.ts';
import { HttpStatusCode } from 'axios';
import { removeParamFromUrl } from '~/utils/commonUtils.ts';

type AgentPortalAuthenticationProps = {
  children: JSXElement;
};

export default function AgentPortalAuthentication(
  props: AgentPortalAuthenticationProps
): JSXElement {
  const merged = mergeProps({}, props);
  const [isLoaded, setIsLoaded] = createSignal(false);

  const params = new URLSearchParams(new URL(window.location.href).search);

  const portalSessionDetailsQuery = useQuery(() => ({
    queryKey: getPortalSessionDetailsQuery(),
    queryFn: async () => await getPortalSessionDetails(),
    retry: retryAuthorizedRequests,
    enabled: false
  }));

  if (params.has(miscConstants.QUERY_PARAMS.CUSTOMER_PORTAL_LINK_ID)) {
    const portalSessionQuery = useQuery(() => ({
      queryKey: getPortalSessionQuery(),
      queryFn: async () =>
        await createPortalSession(
          params.get(miscConstants.QUERY_PARAMS.CUSTOMER_PORTAL_LINK_ID) || ''
        ),
      retry: retryAuthorizedRequests
    }));

    createEffect(() => {
      if (portalSessionQuery.isSuccess) {
        setIsLoaded(true);
        portalSessionQuery.data.accessToken &&
          setGlobalStore(
            'agentPortalAuthToken',
            portalSessionQuery.data.accessToken
          );
        removeParamFromUrl(miscConstants.QUERY_PARAMS.CUSTOMER_PORTAL_LINK_ID);
      } else if (!portalSessionQuery.isPending && portalSessionQuery.isError) {
        if (
          portalSessionQuery.error?.httpStatus === HttpStatusCode.Unauthorized
        ) {
          throw new Error(AppErrors.AGENT_PORTAL_SESSION_EXPIRED);
        }
        throw new Error(AppErrors.HTTP_REQUEST_FAILED);
      }
    });
  } else if (
    params.has(miscConstants.QUERY_PARAMS.CUSTOMER_PORTAL_AUTH_TOKEN)
  ) {
    setGlobalStore(
      'agentPortalAuthToken',
      params.get(miscConstants.QUERY_PARAMS.CUSTOMER_PORTAL_AUTH_TOKEN || '')
    );
    setGlobalStore('shouldForwardAgentPortalToken', true);
    removeParamFromUrl(miscConstants.QUERY_PARAMS.CUSTOMER_PORTAL_AUTH_TOKEN);
    setIsLoaded(true);
  } else {
    setIsLoaded(true);
  }

  // fetched after isLoaded is true to avoid blocking the render path
  createEffect(() => {
    if (isLoaded()) {
      portalSessionDetailsQuery.refetch();
    }
  });

  createEffect(() => {
    if (portalSessionDetailsQuery.isSuccess) {
      const expiryTime =
        portalSessionDetailsQuery.data.sessionExpiry ||
        portalSessionDetailsQuery.data.accessTokenExpiry;

      const expiryTimeDate: Date = new Date(expiryTime);
      setGlobalStore('agentPortalSessionExpiry', expiryTimeDate);
    } else if (
      !portalSessionDetailsQuery.isPending &&
      portalSessionDetailsQuery.isError
    ) {
      if (
        portalSessionDetailsQuery.error?.httpStatus ===
        HttpStatusCode.Unauthorized
      ) {
        throw new Error(AppErrors.AGENT_PORTAL_SESSION_EXPIRED);
      }
      throw new Error(AppErrors.HTTP_REQUEST_FAILED);
    }
  });

  return (
    <Show
      when={isLoaded()}
      fallback={<SkLoader />}
    >
      {merged.children}
    </Show>
  );
}
